import React, { useState, useEffect } from 'react';
import InputWrappingBox from 'components/container/InputWrappingBox';

import { episodeApi } from 'apis/story/episodeApis';
import { theme } from 'utils/style/theme';
import { useTranslation } from 'react-i18next';
import { Radio, Select, message } from 'antd';
import styled from 'styled-components';
import UploadImage from './UploadImage';
import PrimaryButton from 'components/button/PrimaryButton';
import SubmitFlexBox from 'components/container/SubmitFlexBox';
import { useNavigate } from 'react-router-dom';
import { handleMessage } from 'utils/helper/message';
import { liveBackgroundApi } from 'apis/resource/liveBackgroundApis';
import { liveObjApi } from 'apis/resource/liveObjectApis';
import { liveIllustApi } from 'apis/resource/liveIllustApis';
import SelectBox from 'components/SelectBox';
import InputStyled from 'components/InputStyled';
import refreshPage from 'utils/helper/refreshPage';
import { fetchEpisodeList } from 'apis/common/commonApis';
import { ICommonCode } from 'utils/types/storyTypes';

interface IProps {
  data: any;
  setData: React.Dispatch<React.SetStateAction<any>>;
  type: string;
}

const RightSideContent: React.FC<IProps> = ({ data, setData, type }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [charactersList, setCharactersList] = useState<ICommonCode[] | null>(
    null,
  );

  const [epsiodeList, setEpisodeList] = useState<ICommonCode[] | null>(null);

  const handleGetCharactersList = async () => {
    if (data) {
      const connectEpisodeData = await episodeApi.charactersList(
        data.project_id.toString(),
        data.dlc_id ? data.dlc_id.toString() : '-1',
      );
      const episodeListData = await fetchEpisodeList(
        data.project_id.toString(),
        data.dlc_id ? data.dlc_id.toString() : '-1',
      );
      setEpisodeList(episodeListData);

      setCharactersList(connectEpisodeData);
    }
  };

  useEffect(() => {
    handleGetCharactersList();
  }, [data]);

  const handleOnChange = (value, type: string) => {
    if (data) {
      const cloneData = JSON.parse(JSON.stringify(data));
      cloneData[type] = value;
      setData(cloneData);
    }
  };

  const [loading, setLoading] = useState<boolean>(false);
  const handleSubmit = async () => {
    if (data) {
      const hasLanguageDuplicate = data.localizations.some(
        (item, index, array) =>
          array.findIndex((i) => i.lang === item.lang) !== index,
      );
      if (hasLanguageDuplicate) {
        handleMessage(message, 'error', 'You can not select same language');
      } else {
        try {
          const postData = {
            update: data,
          };
          setLoading(true);
          const url =
            type === 'live_bg'
              ? liveBackgroundApi.liveBackgroundUpdate
              : type === 'live_object'
              ? liveObjApi.liveObjectUpdate
              : liveIllustApi.liveIllustUpdate;
          await url(data.project_id.toString(), data.id.toString(), postData);
          setLoading(false);
          handleMessage(message, 'success', 'Success');
          refreshPage();
        } catch (err) {
          console.log('update minicut info error:', err);
          setLoading(false);
        }
      }
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <>
      <InputWrappingBox>
        <Wrapper>
          <SectionWarpper>
            <div className='header'>
              <p>
                {t(
                  'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.live.editPage.id',
                )}
                : <span>{data?.id}</span>
              </p>
            </div>
            <div>
              <p className='title'>
                {t(
                  'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.live.editPage.name',
                )}
              </p>
              <InputStyled
                defaultValue={data?.live_name}
                onChange={(e) => {
                  handleOnChange(e.target.value, 'live_name');
                }}
                maxLength={100}
              />
              <p className='length-subtext'>
                {typeof data?.image_name?.length === 'number'
                  ? `${data?.image_name?.length}/100 characters`
                  : '0/100 characters'}
              </p>
            </div>
            <div>
              <p className='title'>{t('updateMinicut.form.character')}</p>
              <SelectBox
                value={data?.speaker}
                onChange={(e) => {
                  handleOnChange(e, 'speaker');
                }}
                options={charactersList?.map((v, i) => {
                  return {
                    value: v.code,
                    label: v.code_name,
                  };
                })}
              />
            </div>
            <div>
              <p className='title'>{t('updateMinicut.form.episode')}</p>
              <SelectBox
                value={data?.appear_episode}
                onChange={(e) => {
                  handleOnChange(e, 'appear_episode');
                }}
                options={epsiodeList?.map((v, i) => {
                  return {
                    value: v.code,
                    label: v.code_name,
                  };
                })}
              />
            </div>
            <div>
              <p className='title'>{t('updateMinicut.form.status')}</p>
              <Radio.Group
                onChange={(e) => {
                  handleOnChange(e.target.value, 'is_public');
                }}
                value={data?.is_public}
              >
                <Radio value={true}>Public</Radio>
                <Radio value={false}>Private</Radio>
              </Radio.Group>
            </div>
            <div>
              <p className='title'>{t('updateMinicut.form.xOffset')}</p>
              <InputStyled
                defaultValue={data?.offset_x}
                onChange={(e) => {
                  handleOnChange(Number(e.target.value), 'offset_x');
                }}
                type='number'
                maxLength={30}
              />
            </div>
            <div>
              <p className='title'>{t('updateMinicut.form.yOffset')}</p>
              <InputStyled
                defaultValue={data?.offset_y}
                onChange={(e) => {
                  handleOnChange(Number(e.target.value), 'offset_y');
                }}
                type='number'
                maxLength={30}
              />
            </div>
            <div>
              <p className='title'>{t('updateMinicut.form.gameScale')}</p>
              <InputStyled
                defaultValue={data?.game_scale}
                onChange={(e) => {
                  handleOnChange(Number(e.target.value), 'game_scale');
                }}
                type='number'
                maxLength={30}
              />
            </div>
            <div>
              <p className='title'>{t('updateMinicut.form.thumbnail')}</p>
              <UploadImage
                data={data}
                setData={setData}
                type={type}
                isPublic={data?.is_public}
              />
            </div>
          </SectionWarpper>
        </Wrapper>
      </InputWrappingBox>
      <SubmitFlexBox style={{ columnGap: 12, marginTop: '24px' }}>
        <>
          <PrimaryButton
            text={t('updateStory.form.buttonCancel')}
            height='40px'
            width='151px'
            bgColor='#FFFF'
            textColor={theme.colors.dark_400}
            fontWeight={theme.fontWeights.medium}
            fontSize={theme.fontSizes.normal}
            border={`1px solid ${theme.colors.gray_6}`}
            rounded='6px'
            hoverBgColor={'#FFFF'}
            activeBgColor={'#FFFF'}
            selectedBgColor={'#FFFF'}
            onClick={handleGoBack}
          />
          <PrimaryButton
            text={t('updateStory.form.buttonSubmit')}
            height='40px'
            width='151px'
            bgColor={theme.colors.blue_500}
            textColor='#ffffff'
            fontWeight={theme.fontWeights.medium}
            fontSize={theme.fontSizes.normal}
            border={`1px solid ${theme.colors.blue_500}`}
            rounded='6px'
            hoverBgColor={theme.colors.blue_600}
            activeBgColor={theme.colors.blue_800}
            selectedBgColor={theme.colors.blue_700}
            onClick={handleSubmit}
            loading={loading}
          />
        </>
      </SubmitFlexBox>
    </>
  );
};

export default RightSideContent;

const Wrapper = styled.div`
  & > * + * {
    margin-top: 16px;
  }
  .title {
    font-size: ${theme.fontSizes.sm};
    font-style: normal;
    font-weight: ${theme.fontWeights.normal};
    line-height: 20px;
    letter-spacing: 0.2px;
  }
  .header {
    display: flex;
    align-items: center;
    p {
      color: ${theme.colors.dark_500};
      font-size: ${theme.fontSizes.sm};
      font-style: normal;
      font-weight: ${theme.fontWeights.normal};
      line-height: 20px; /* 166.667% */
      letter-spacing: 0.2px;
    }
    span {
      font-weight: ${theme.fontWeights.bold};
    }
  }
  .header p {
    font-size: ${theme.fontSizes.sm};
    font-style: normal;
    font-weight: ${theme.fontWeights.normal};
    line-height: 20px;
    letter-spacing: 0.2px;
    color: ${theme.colors.dark_500};
  }
  .header p span {
    font-weight: ${theme.fontWeights.bold};
  }
  .fixed {
    display: flex;
    align-items: center;
    justify-content: center;
    & > * + * {
      margin-left: 10px;
    }
  }
  .condition {
    display: flex;
    align-items: center;
    justify-content: space-between;
    & > * + * {
      margin-left: 50px;
    }
  }
`;

const SectionWarpper = styled.div`
  & > * + * {
    margin-top: 24px;
  }
  .ant-input-disabled {
    color: ${theme.colors.dark_300};
  }
`;
